import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { fromEvent, merge } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel } from '../../../../../../core/_base/crud';
import {
  Client,
  ClientOnServerDeleted,
  ClientOnServerUpdated,
  ClientsDataSource,
  ClientsPageRequested,
  ViewContext,
} from '../../../../../../core/lists';
import { TranslateService } from '@ngx-translate/core';
import { Update } from '@ngrx/entity';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { TabletUtil } from '../../../../../../core/utils';

@Component({
  selector: 'kt-clients-list',
  templateUrl: './clients-list.component.html',
  styleUrls: ['./clients-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientsListComponent implements OnInit, OnDestroy {
  dataSource: ClientsDataSource;
  displayedColumns = [
    'clientClass',
    'lastName',
    'email',
    'domain',
    'cellPhone',
    'fax',
    'isActive',
    'actions',
  ];
  searchBarColumns = [
    'searchClientClass',
    'searchName',
    'searchEmail',
    'searchDomain',
    'searchCellPhone',
    'searchFax',
    'searchIsActive',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  searchNameInput = '';
  searchEmailInput = '';
  searchCellPhoneInput = '';
  searchFaxInput = '';
  searchClientClassInput = '';
  searchDomainInput = '';
  searchIsActiveSelect = 'true';
  statusTracker = 0;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  private subscriptions: any[] = [];
  searchForm: FormGroup;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize: any;

  constructor(
    private navigationService: NavigationService,
    private permissionsService: NgxPermissionsService,
    private tabletUtil: TabletUtil,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private translate: TranslateService,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit() {
    // Comment by Nourhan: please don't change the order of this line
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'id';
      this.sort.direction = this.sort.direction || 'desc';
    });
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        tap(() => {
          this.loadClientsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadClientsList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });

    this.dataSource = new ClientsDataSource(this.store);

    this.searchForm = this.fb.group({
      search: [],
      status: ['true'],
    });

    // Comment by Nourhan: please don't change the order of this scope
    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied.pageSize;
    }

    const routeSubscription = this.activatedRoute.queryParams.subscribe(() => {
      this.loadClientsList();
    });
    this.subscriptions.push({ id: 5, sub: routeSubscription });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  loadClientsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'lastName',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(new ClientsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?: any): any {
    const filter: any = {};
    filter.isActive = this.searchIsActiveSelect;
    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.isActive = appliedFilters.isActive;
      this.searchIsActiveSelect = appliedFilters.isActive;
      this.showSearchBar = appliedFilters.showSearchBar;
      if (this.showSearchBar) {
        this.searchNameInput = appliedFilters.name;
        this.searchEmailInput = appliedFilters.email;
        this.searchCellPhoneInput = appliedFilters.cell_phone;
        this.searchFaxInput = appliedFilters.fax;
        this.searchDomainInput = appliedFilters.domain;
        this.searchClientClassInput = appliedFilters.client_class;
      } else {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = appliedFilters.searchTerm || '';
        }
      }
      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'lastName';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;
    if (this.showSearchBar) {
      filter.name = this.searchNameInput;
      filter.email = this.searchEmailInput;
      filter.domain = this.searchDomainInput;
      filter.client_class = this.searchClientClassInput;
      filter.cell_phone = this.searchCellPhoneInput?.replace(/[()\-\s]/g, '');
      filter.fax = this.searchFaxInput?.replace(/[()\-\s]/g, '');
      filter.showSearchBar = true;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
      filter.showSearchBar = false;
    }
    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'lastName';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  deleteClient(_item: Client) {
    const _title: string = this.translate.instant('CLIENT.DELETE_CLIENT_SIMPLE.TITLE');
    const _description: string = this.translate.instant('CLIENT.DELETE_CLIENT_SIMPLE.DESCRIPTION');
    const _waitDescription: string = this.translate.instant(
      'CLIENT.DELETE_CLIENT_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.store.dispatch(new ClientOnServerDeleted({ id: _item.id }));
    });
  }

  updateStatusForClient(client: Client) {
    const _title: string = this.translate.instant('CLIENT.UPDATE_STATUS_SIMPLE.TITLE');
    const _description: string = this.translate.instant('CLIENT.UPDATE_STATUS_SIMPLE.DESCRIPTION');
    const _waitDescription: string = this.translate.instant(
      'CLIENT.UPDATE_STATUS_SIMPLE.WAIT_DESCRIPTION',
    );

    const dialogRef = this.layoutUtilsService.confirmAction(_title, _description, _waitDescription);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const newClient: Client = Object.assign(new Client(), client);
      newClient.changeActiveStatus();
      const updateClient: Update<Client> = {
        id: newClient.id,
        changes: newClient,
      };
      this.store.dispatch(
        new ClientOnServerUpdated({
          partialClient: updateClient,
          client: newClient,
        }),
      );
    });
  }

  canViewClient(): boolean {
    return !!this.permissionsService.getPermission('canViewClient');
  }

  canEditClient(): boolean {
    return !!this.permissionsService.getPermission('canEditClient');
  }

  createClient() {
    this.navigationService.navigateTo('lists/clients/add');
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewClient(client: Client) {
    if (!this.canViewClient()) return;

    if (this.canEditClient()) {
      this.editClient(client.id);
      return;
    }
    const queryParams = {
      viewContext: ViewContext.clients,
    };
    this.navigationService.navigateTo('lists/clients/view/' + client.id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  viewClientDblclick(ref: string, client: Client): void {
    this.tabletUtil.handleDblclick(ref, this.viewClient.bind(this, client));
  }

  editClient(id: number) {
    const queryParams = {
      viewContext: ViewContext.clients,
    };

    this.navigationService.navigateTo('lists/clients/edit/' + id, queryParams);
    this.navigationService.updateURLWithFiltersOnList(this.filterConfiguration(true));
  }

  getItemCssClassByStatus(status: string): string {
    switch (status) {
      case 'false':
        return 'danger';
      case 'true':
        return 'success';
      default:
        return 'warning';
    }
  }

  
  getItemStatusString(status: string): string {
    switch (status) {
      case 'false':
        return this.translate.instant('GLOBAL.INACTIVE');
      case 'true':
        return this.translate.instant('GLOBAL.ACTIVE');
      default:
        return this.translate.instant('GLOBAL.ALL');
    }
  }

  reloadClientsList(): void {
    this.paginator.pageIndex = 0;
    this.loadClientsList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    this.statusTracker = (this.statusTracker + 1) % 3;

    switch (this.statusTracker) {
      case 0:
        this.searchIsActiveSelect = 'true';
        break;
      case 1:
        this.searchIsActiveSelect = 'false';
        break;
      case 2:
        this.searchIsActiveSelect = '';
        break;
    }

    this.reloadClientsList();
  }

  handleStatusChange(): void {
    switch (this.searchIsActiveSelect) {
      case 'true':
        this.statusTracker = 0;
        break;
      case 'false':
        this.statusTracker = 1;
        break;
      case '':
        this.statusTracker = 2;
        break;
    }
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();
    this.searchNameInput = '';
    this.searchEmailInput = '';
    this.searchDomainInput = '';
    this.searchClientClassInput = '';
    this.searchCellPhoneInput = '';
    this.searchFaxInput = '';
  }

  resetSearchInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadClientsList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
    } else {
      this.resetSearchBar();
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadClientsList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();
    this.reloadClientsList();
  }
}
